import { Maybe, OrgId, Site, StateAssert } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { isEmpty } from 'lodash';
import {
  StudyFileQueryV1,
  StudyFileQueryCriterionV1,
  StudyFileSortFieldsV1,
  StudyFileQueryTypeV1,
  StudyFileDateRangeFieldsV1,
  StudyFileQueryOperatorV1,
  StudyFileCriteriaFieldsV1,
  StudyFileQuerySortOrderV1,
} from '@csp/dmdp-api-file-storage-studydocs-dto';
import { RoleType } from '@csp/dmdp-api-common-dto';
import { StudyDocumentsQueryFilter, StudyDocumentsQueryFilterRoles } from '../model/StudyDocumentsQueryFilter';

const getOrgIds = (criteria: StudyDocumentsQueryFilter['criteria'], sites: Site[]): Maybe<OrgId[]> => {
  if (criteria.site) {
    StateAssert.isDefined(criteria.country, 'Unexpeted undefined country in filter');
    // When a site is selected, return the OrgIds of the site, country and study.
    return [...criteria.country.orgIdsIncludingParents, criteria.site.orgId];
  } else if (criteria.country) {
    // When only a country is selected, return the OrgIds of the country, all its sites and the study.
    const siteOrgIds = sites
      .filter(site => criteria.country?.orgId && site.isChildOf(criteria.country?.orgId))
      .map(({ orgId }) => orgId);

    return [...criteria.country.orgIdsIncludingParents, ...siteOrgIds];
  }

  return undefined;
};

const byFilter = (
  { criteria, dateRangeCriteria }: StudyDocumentsQueryFilter,
  sites: Site[],
  baseCriterions: StudyFileQueryCriterionV1[] = [],
): StudyFileQueryV1 => {
  StateAssert.isTrue(!isEmpty(criteria), 'Filter criteria must be set');

  const fieldCriterias = Object.keys(criteria)
    .map(filterKey => {
      switch (filterKey) {
        case 'fileName':
          return criteria.fileName?.length
            ? {
                fieldCriterion: {
                  field: StudyFileCriteriaFieldsV1.FILE_NAME,
                  type: StudyFileQueryTypeV1.LIKE,
                  values: [criteria.fileName],
                },
              }
            : undefined;
        case 'roles':
          return criteria.roles === StudyDocumentsQueryFilterRoles.GSTM_AND_CRA
            ? {
                fieldCriterion: {
                  field: StudyFileCriteriaFieldsV1.ROLES,
                  type: StudyFileQueryTypeV1.IN,
                  values: [RoleType.CRA, RoleType.GSTM],
                },
              }
            : undefined;
        case 'folders':
          return criteria.folders?.length
            ? {
                fieldCriterion: {
                  field: StudyFileCriteriaFieldsV1.CLASSIFICATION,
                  type: StudyFileQueryTypeV1.IN,
                  values: criteria.folders,
                },
              }
            : undefined;
        case 'country':
        case 'site':
          // OrgIds are added below
          return undefined;
        default:
          throw new Error(`Invalid filter key: ${filterKey}`);
      }
    })
    .filter(isDefined);

  const orgIds = getOrgIds(criteria, sites);
  if (orgIds) {
    fieldCriterias.push({
      fieldCriterion: {
        field: StudyFileCriteriaFieldsV1.ORG_IDS,
        type: StudyFileQueryTypeV1.IN,
        values: orgIds,
      },
    });
  }

  return {
    dateRangeCriteria:
      dateRangeCriteria.startDate || dateRangeCriteria.endDate
        ? [
            {
              field: StudyFileDateRangeFieldsV1.CREATED_AT,
              from: dateRangeCriteria.startDate,
              to: dateRangeCriteria.endDate,
              toInclusive: true,
              fromInclusive: true,
            },
          ]
        : undefined,
    criterion: {
      operator: StudyFileQueryOperatorV1.AND,
      criteria: [...baseCriterions, ...fieldCriterias],
    },
    sort: [
      {
        field: StudyFileSortFieldsV1.CREATED_AT,
        order: StudyFileQuerySortOrderV1.DESC,
      },
    ],
  };
};

export const StudyDocumentsQueryMapper = { byFilter };
